import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Table, Modal, Form, Pagination } from "react-bootstrap";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/TopBar";
import axiosInstance from "../../components/AxiosInstance";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import { FaEdit, FaTrash } from "react-icons/fa";
import BackButton from "../../components/BackButton";

export default function PackageCategory() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  // ─ Optional occupancy fields — empty string = "not specified". They
  //   round-trip as null to the backend when blank.
  const [adults, setAdults] = useState("");
  const [children, setChildren] = useState("");
  const [childAge, setChildAge] = useState("");
  const [occupancy, setOccupancy] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);

  // Validation functions
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!name || name.trim() === "") {
      newErrors.name = "Package Category Name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Package Category Name must be at least 2 characters long";
    } else if (name.trim().length > 100) {
      newErrors.name = "Package Category Name must not exceed 100 characters";
    }

    // Code validation
    if (!code || code.trim() === "") {
      newErrors.code = "Package Category Code is required";
    } else if (code.trim().length < 1) {
      newErrors.code = "Package Category Code must be at least 1 character long";
    } else if (code.trim().length > 20) {
      newErrors.code = "Package Category Code must not exceed 20 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const nextId = useMemo(
    () => Math.max(0, ...items.map((i) => i.id)) + 1,
    [items]
  );

  const openCreate = () => {
    setEditing(null);
    setName("");
    setCode("");
    setAdults("");
    setChildren("");
    setChildAge("");
    setOccupancy("");
    setError("");
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setName(item.name || "");
    setCode(item.code || "");
    setAdults(item.adults === null || item.adults === undefined ? "" : String(item.adults));
    setChildren(item.children === null || item.children === undefined ? "" : String(item.children));
    setChildAge(item.childAge || "");
    // occupancy is numeric — `|| ""` would discard 0, so use a strict null check.
    setOccupancy(item.occupancy === null || item.occupancy === undefined ? "" : String(item.occupancy));
    setError("");
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = async () => {
    if (!editing) return;

    // Validate form before submitting
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    try {
      setIsLoading(true);
      const editRes = await axiosInstance.put(
        `/api/packageCategory/${editing.packageCategoryId}`,
        {
          name: name.trim(),
          code: code.trim(),
          // Send null when blank so the backend column stays NULL rather
          // than storing the string "" or NaN.
          adults: adults === "" ? null : Number(adults),
          children: children === "" ? null : Number(children),
          childAge: childAge.trim() || null,
          occupancy: occupancy === "" ? null : Number(occupancy),
        }
      );

     if (editRes.data) {
        toast.success("Package Category Updated Successfully!");
        // First refresh the list
        await fetchPackageCategoryList(page, search);
        // Then close modal and reset state
        closeModal();
      }
    } catch (error) {
      setError("Failed to update package category");
      toast.error("Failed to update package category");
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setName("");
    setCode("");
    setAdults("");
    setChildren("");
    setChildAge("");
    setOccupancy("");
    setError("");
    setErrors({});
  };

  const fetchPackageCategoryList = async (pageNum = 0, searchTerm = search) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: "10",
      });

      if (searchTerm && searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      const res = await axiosInstance.get(
        `/api/packageCategory?${params.toString()}`
      );
     
     // Check if response has data and pagination info
     if (res.data && Array.isArray(res.data)) {
        setItems(res.data);
        // Since backend doesn't return totalPages, we'll calculate it based on data length
        // If we get less than 10 items, it's likely the last page
        if (res.data.length < 10) {
          setTotalPages(pageNum + 1);
        } else {
          // If we get exactly 10 items, there might be more pages
          // We'll set a reasonable total or keep the current totalPages
          setTotalPages(Math.max(totalPages, pageNum + 2));
        }

        setPage(pageNum);
      } else {
        setItems([]);
        setTotalPages(0);
        setPage(0);
      }
    } catch (err) {
      toast.error("Failed to load package categories");
      setItems([]);
      setTotalPages(0);
      setPage(0);
    } finally {
      setIsLoading(false);
    }
  };

  const savePackageCategory = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    try {
      setIsLoading(true);
      const packageCategoryPayload = {
        name: name.trim(),
        code: code.trim(),
        adults: adults === "" ? null : Number(adults),
        children: children === "" ? null : Number(children),
        childAge: childAge.trim() || null,
        occupancy: occupancy === "" ? null : Number(occupancy),
      };
      const packageCategorySaveRes = await axiosInstance.post(
        "/api/packageCategory/save",
        packageCategoryPayload
      );
      if (packageCategorySaveRes.data !== 0) {
        toast.success("Package Category added Successfully!");
        // First refresh the list
        await fetchPackageCategoryList(page, search);
        // Then close modal
        closeModal();
      }
    } catch (error) {
      setError("Sorry! Data not saved to db..");
      toast.error("Failed to save package category data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPackageCategoryList();
  }, []);

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for search
    if (search !== "") {
      const timeout = setTimeout(() => {
        fetchPackageCategoryList(0, search);
      }, 500); // 500ms delay
      setSearchTimeout(timeout);
    } else if (search === "") {
      // If search is cleared, fetch all data
      fetchPackageCategoryList(0, "");
    }

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [search]);

  const handleDelete = (item) => {
    console.log("delete item :::" , item);
    Swal.fire({
      title: `Are you sure? You want to delete ${item.name} Package Category`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      customClass: {
        popup: "swal-small",
        title: "swal-small-title",
        htmlContainer: "swal-small-text",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        axiosInstance
          .delete(`/api/packageCategory/${item.packageCategoryId}`)
          .then(() => {
            toast.success("Package Category deleted successfully");
            fetchPackageCategoryList(page, search);
          })
          .catch(() => {
            toast.error("Sorry!! Package Category not deleted");
          });
      }
    });
  };

  return (
    <div className="min-vh-100 bg-light d-flex flex-column">
      <Topbar />
      <div className="d-flex flex-grow-1">
        <Sidebar />
        <main className="flex-grow-1 p-4">
          <Card className="shadow-sm rounded-xl">
            <Card.Header className="d-flex flex-column flex-sm-row gap-2 justify-content-between align-items-stretch align-items-sm-center">
              <span className="d-flex align-items-center gap-2">
                <BackButton fallback="/adminDashboard" />
                <span className="fw-semibold">Package Category</span>
              </span>
              {/* Package Category Search */}
               <Form.Group className="hotel-search-bar flex-grow-1 flex-sm-grow-0">
                  <Form.Control
                    type="text"
                    placeholder="Search package category...."
                    className="form-control-modern-sm"
                    value={searchTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchTerm(value);
                      fetchPackageCategoryList(0, value); // pass value to API
                    }}
                  />
                </Form.Group>
              <Button className="btn-green" onClick={openCreate}>
                + Create
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover striped className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>S/N</th>
                    <th>Package Category Name</th>
                    <th>Code</th>
                    <th style={{ width: 90 }}>Adults</th>
                    <th style={{ width: 90 }}>Children</th>
                    <th>Child Age</th>
                    <th>Occupancy</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.PackageCategoryId}>
                      <td>{index + 1 + page * 10}</td>
                      <td>{item.name}</td>
                      <td>{item.code}</td>
                      <td>{item.adults ?? "-"}</td>
                      <td>{item.children ?? "-"}</td>
                      <td>{item.childAge || "-"}</td>
                      <td>{item.occupancy ?? "-"}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <FaEdit
                            className="text-primary"
                            style={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() => openEdit(item)}
                            title="Edit"
                          />
                          <FaTrash
                            className="text-danger"
                            style={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() => handleDelete(item)}
                            title="Delete"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {isLoading && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading available package categories...
                      </td>
                    </tr>
                  )}
                  {items.length === 0 && !isLoading && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        No package categories found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div>
                    <small className="text-muted">
                      Showing {items.length} of {totalPages * 10} package categories
                    </small>
                  </div>
                  <div>
                    <Pagination className="mb-0">
                      <Pagination.Prev
                        disabled={page === 0}
                        onClick={() => fetchPackageCategoryList(page - 1, search)}
                      />
                      {[...Array(totalPages).keys()].map((num) => (
                        <Pagination.Item
                          key={num}
                          active={num === page}
                          onClick={() => fetchPackageCategoryList(num, search)}
                        >
                          {num + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        disabled={page === totalPages - 1}
                        onClick={() => fetchPackageCategoryList(page + 1, search)}
                      />
                    </Pagination>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Modal show={showModal} onHide={() => {}} centered backdrop="static" keyboard={false}>
            <Modal.Header closeButton={!isLoading} onHide={closeModal}>
              <Modal.Title>
                {editing ? "Update Package Category" : "Create Package Category"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Package Category Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      clearError('name');
                    }}
                    placeholder="Enter package category name"
                    autoFocus
                    isInvalid={!!errors.name}
                    maxLength={100}
                  />
                  {errors.name && (
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Package Category Code <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      clearError('code');
                    }}
                    placeholder="Enter package category code"
                    isInvalid={!!errors.code}
                    maxLength={20}
                  />
                  {errors.code && (
                    <Form.Control.Feedback type="invalid">
                      {errors.code}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {/* ─ Optional occupancy fields ─────────────────────── */}
                <div className="row">
                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Adults</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={adults}
                      onChange={(e) => setAdults(e.target.value)}
                      placeholder="e.g. 2"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Children</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={children}
                      onChange={(e) => setChildren(e.target.value)}
                      placeholder="e.g. 1"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Child Age</Form.Label>
                    <Form.Control
                      type="text"
                      value={childAge}
                      onChange={(e) => setChildAge(e.target.value)}
                      placeholder="e.g. 3-12 years"
                      maxLength={100}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3 col-md-6">
                    <Form.Label>Occupancy</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={occupancy}
                      onChange={(e) => setOccupancy(e.target.value)}
                      placeholder="e.g. 2"
                    />
                  </Form.Group>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={closeModal}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="btn-indigo"
                onClick={editing ? handleEdit : savePackageCategory}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {editing ? "Updating..." : "Saving..."}
                  </>
                ) : editing ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </main>
      </div>
    </div>
  );
}